import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/DeviceTransfer.css';
import CustomButton from '../CustomElements/CustomButton';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTextarea from '../CustomElements/CustomTextarea';
import CustomTitle from '../CustomElements/CustomTitle';
import FormContainer from '../CustomElements/FormContainer';
import CustomContainer from '../CustomElements/CustomContainer';
import FilePicker from '../FileManager/FilePicker';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
import useSuccessListener from '../../customHook/useSuccessListener';
// Утилиты
import { getCurrentStatusId } from '../../utils/getCurrentStatusId';
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { handleUniversalChange } from '../../utils/formUtils';
import { omitFields } from '../../utils/omitFields';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';
// Действия Redux
import { transferDeviceForStorage } from '../../redux/actions/devices.action';
import { load } from '../../redux/actions/loader.action';
import { editStorageStatusFromServer } from '../../redux/actions/statuses.action';

// Этот компонент отображает форму для передачи устройства на другой склад
// или редактирование статуса хранение устройства.

function TransferDevice({ currentDevice, closeModal, currentStatus, isEdit, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // получаем опции из стора
  const optionsForSelect = useSelector((state) => state.options);

  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);

  // Получение сообщения об ошибке из глобального состояния
  const error = useSelector((state) => state.error);

  // Состояния для отображения модального окна подтверждения
  const [addSuccess, setAddSuccess] = useState(false);

  // Состояние для формы. Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () =>
      currentStatus
        ? {
            id: currentStatus?.id,
            deviceId: currentDevice?.id,
            fromStatusId: currentStatus?.fromStatusId,
            date: convertToDate(currentStatus?.date),
            storageId: currentStatus?.storageId || '',
            senderId: currentStatus.senderId || '',
            isBroken: currentStatus?.broken,
            acceptanceCert: currentStatus?.acceptanceCert,
            acceptanceCertName: currentStatus?.acceptanceCertName,
            receiverId: currentStatus?.receiverId,
            note: currentStatus?.note || '',
          }
        : {
            deviceId: currentDevice.id,
            fromStatusId: getCurrentStatusId(currentDevice?.status, optionsForSelect.deviceStatuses),
            date: '',
            storageId: '',
            senderId: '',
            isBroken: undefined,
            acceptanceCert: '',
            receiverId: '',
            note: '',
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStatus]
  );

  const [formData, setFormData] = useState(initialFormData);

  // Обновляем formData при изменении currentStatus
  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  //проверяем были ли изменения в форме (true-да, true-нет)
  const isFormEdit = useFormDataChanged(initialFormData, formData, isEdit);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, ['note']);

  // отмена подтверждения
  const closeStatusEditModal = () => {
    setAddSuccess(false);
  };

  // Обработчик отправки формы передачи на другой склад
  const handlerSubmit = (event) => {
    event.preventDefault();

    // При редактировании открываем модальное окно подтверждения
    if (isEdit) {
      setAddSuccess(true);
      return;
    }
    // Переход в состояние загрузки
    dispatch(load());
    // Удаляем лишние поля, не подлежащие отправке
    const formWithoutExcludedFields = omitFields(formData, ['deviceId', 'acceptanceCertName']);
    // Удаляем поля с пустыми строками
    const formWithoutEmptyStrings = removeEmptyStrings(formWithoutExcludedFields);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutEmptyStrings, ['date']);
    // Отправка данных
    dispatch(
      transferDeviceForStorage({
        payload,
        deviceId: formData?.deviceId,
        type: currentDevice?.typeId,
        path: 'storage',
      })
    );
  };

  // Обработчик отправки формы редактирования статуса хранение
  const handlerSubmitEdit = (event) => {
    event.preventDefault();
    //переводим в состояние загрузки
    dispatch(load());
    // сравниваем начальное и текущее состояние заменяем на null пустые строки для удаленных полей
    const formDataWithEmptyStrings = prepareObjectForSubmit(initialFormData, formData);
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcludedFields = omitFields(formDataWithEmptyStrings, ['id', 'deviceId', 'acceptanceCertName']);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutExcludedFields, ['date']);
    // Отправляем данные на сервер через Redux
    dispatch(
      editStorageStatusFromServer({
        payload,
        deviceId: formData?.deviceId,
        statusId: formData?.id,
        type: currentDevice?.typeId,
        path: 'storage',
      })
    );
  };

  //отслеживаем выполнение запроса
  useSuccessListener(setFormData, initialFormData, closeModal);

  useEffect(() => {
    // Если нет ошибок, закрываем модальное окно
    if (!loader?.isLoading && loader?.isSuccess) {
      closeStatusEditModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess, error]);

  return (
    <CustomContainer>
      <CustomTitle
        titleText={
          isEdit
            ? t?.deviceEditStatusTitle || 'ИЗМЕНЕНИЕ ДАННЫХ СТАТУСА'
            : t?.deviceTransferTitle || 'ПЕРЕМЕЩЕНИЕ НА ДРУГОЙ СКЛАД УСТРОЙСТВА'
        }
        deviceType={currentDevice?.type}
        deviceId={currentDevice?.id}
      />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.statusChangeConfirmationText || 'Вы уверены, что хотите изменить данные статуса?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmitEdit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={closeStatusEditModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <DateSelector
            column="left"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={t?.labelTransferDate || 'Дата передачи *'}
            selectedDate={formData?.date}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'date', date, 'date')}
            name="date"
            placeholder={t?.placeholderSelectDate || 'Выберите дату'}
            minDate={convertToDate(currentDevice?.lastActionDate)}
            maxDate={new Date()}
          />
          <Selector
            column="left"
            options={optionsForSelect?.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'receiverId', selectedOption, 'select')
            }
            value={optionsForSelect?.employees?.find((option) => option.value === formData.receiverId)}
            label={t?.labelReceiverName || 'ФИО принявшего *'}
            placeholder={t?.placeholderReceiverName || 'Выберите ФИО принявшего'}
            name="receiverId"
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
            rowSpan={2}
          />
          <Selector
            column="right"
            options={optionsForSelect?.storages}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'storageId', selectedOption, 'select')
            }
            placeholder={t?.placeholderStorageLocation || 'Выберите место хранения'}
            label={t?.labelNewStorageLocation || 'Новое место хранения *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect?.storages?.find((option) => option?.value === formData?.storageId)}
            isMulti={false}
            name="storageId"
          />
          <Selector
            column="right"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'senderId', selectedOption, 'select')
            }
            value={optionsForSelect?.employees?.find((option) => option.value === formData.senderId)}
            label={t?.labelSenderName || 'ФИО передавшего *'}
            placeholder={t?.placeholderSenderName || 'Выберите ФИО передавшего'}
            name="senderId"
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
          />
          <Selector
            column="right"
            options={optionsForSelect?.condition?.filter((item) => item.value !== undefined)}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'isBroken', selectedOption, 'select')
            }
            value={optionsForSelect?.condition?.find((option) => option.value === formData.isBroken)}
            label={t?.labelCondition || 'Состояние *'}
            placeholder={t?.placeholderCondition || 'Не определено'}
            name="isBroken"
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
          />
          <CustomTextarea
            placeholder={t?.placeholderComment || 'Оставьте комментарий'}
            label={t?.labelComment?.replace(/\*$/, '').trim() || 'Комментарий'}
            type="textArea"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="note"
            width="100%"
            columnspan={2}
            // height="100%"
            value={formData.note}
          />

          <CustomButton
            onClick={(event) => {
              handlerSubmit(event);
            }}
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            color={isButtonDisabled ? '#ADADAD' : ''}
            text={
              isEdit ? t?.buttonSaveChanges || 'Сохранить изменения' : t?.buttonTransferDevice || 'Передать устройство'
            }
            disabled={isButtonDisabled || !isFormEdit}
            isButton
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default TransferDevice;
